import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import {
  GitBranch,
  Database,
  Server,
  CheckCircle2,
  Lock,
  ShieldCheck,
  Activity,
  ArrowDown,
  Sparkles,
} from 'lucide-react';
import { getBzUserAvatar, getBzUserInitials } from '../utils/bzUserAvatars';
import type { BotAccessDatabase, BotAccessGitHubRepo, BotAccessUser } from './botAccessTypes';

export type BotAccessDiagramViewProps = {
  botName: string;
  botAvatar?: string;
  users: BotAccessUser[];
  usersLoading: boolean;
  repos: BotAccessGitHubRepo[];
  reposTotal: number;
  databases: BotAccessDatabase[];
  databasesTotal: number;
  githubSummary?: string;
  databaseSummary?: string;
  matchedRole?: string | null;
  verifiedCount: number;
  privateCount: number;
  ownerCount: number;
  connectCount: number;
};

export default function BotAccessDiagramView({
  botName,
  botAvatar,
  users,
  usersLoading,
  repos,
  reposTotal,
  databases,
  databasesTotal,
  githubSummary,
  databaseSummary,
  matchedRole,
  verifiedCount,
  privateCount,
  ownerCount,
  connectCount,
}: BotAccessDiagramViewProps) {
  return (
    <div className="diagram-access-root relative">
      <style>{`
        @keyframes diagram-flow {
          0% { stroke-dashoffset: 24; opacity: 0.35; }
          50% { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 0.35; }
        }
        @keyframes diagram-scan {
          0% { transform: translateY(-120%); opacity: 0; }
          15% { opacity: 0.12; }
          100% { transform: translateY(120%); opacity: 0; }
        }
        @keyframes diagram-border-spin {
          to { --diagram-angle: 360deg; }
        }
        .diagram-access-root {
          --diagram-angle: 0deg;
        }
        .diagram-glow-card {
          background: linear-gradient(#0a0f1e, #0a0f1e) padding-box,
            linear-gradient(var(--diagram-angle), #8b5cf6, #6366f1, #34d399, #8b5cf6) border-box;
          border: 1px solid transparent;
          animation: diagram-border-spin 8s linear infinite;
        }
        .diagram-flow-line {
          stroke-dasharray: 8 6;
          animation: diagram-flow 2.4s linear infinite;
        }
        .diagram-scan-line {
          animation: diagram-scan 5s ease-in-out infinite;
        }
      `}</style>

      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl">
        <div className="diagram-scan-line absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-violet-400/20 to-transparent" />
        <div className="absolute left-1/2 top-1/3 h-64 w-64 -translate-x-1/2 rounded-full bg-indigo-600/15 blur-[100px]" />
        <div className="absolute bottom-0 left-0 h-48 w-48 rounded-full bg-violet-600/10 blur-[80px]" />
        <div className="absolute bottom-0 right-0 h-48 w-48 rounded-full bg-emerald-600/10 blur-[80px]" />
      </div>

      <div className="relative space-y-2">
        <div className="flex items-center justify-center gap-2 text-center">
          <Sparkles className="h-4 w-4 text-violet-400" />
          <p className="text-sm text-slate-300">
            Diagramme complet — <span className="font-bold text-violet-300">{users.length}</span> utilisateurs ·{' '}
            <span className="font-bold text-indigo-300">{reposTotal}</span> dépôts ·{' '}
            <span className="font-bold text-emerald-300">{databasesTotal}</span> bases
          </p>
          <Sparkles className="h-4 w-4 text-violet-400" />
        </div>

        <DiagramWires userCount={users.length} />

        <section className="relative z-10 pt-2">
          <DiagramSectionTitle accent="violet">Utilisateurs → {botName}</DiagramSectionTitle>
          {usersLoading && users.length === 0 ? (
            <p className="py-10 text-center text-slate-400">Chargement…</p>
          ) : null}
          {!usersLoading && users.length === 0 ? (
            <p className="py-10 text-center text-slate-400">Aucun utilisateur autorisé.</p>
          ) : (
            <div className="flex flex-wrap justify-center gap-4">
              {users.map((user, index) => (
                <DiagramUserCard key={user.id} user={user} botName={botName} index={index} />
              ))}
            </div>
          )}
        </section>

        <FlowConnector />

        <section className="relative z-10 flex justify-center py-2">
          <DiagramHub botName={botName} botAvatar={botAvatar} />
        </section>

        <FlowConnector />

        <section className="relative z-10 grid gap-6 lg:grid-cols-2">
          <InfraStack
            tone="indigo"
            icon={GitBranch}
            title="GitHub"
            count={reposTotal}
            summary={githubSummary}
            footer={[
              { icon: GitBranch, label: 'Dépôts', value: reposTotal },
              { icon: CheckCircle2, label: 'Vérifiés', value: verifiedCount },
              { icon: Lock, label: 'Privés', value: privateCount },
            ]}
          >
            {repos.length === 0 ? (
              <EmptyInfra message="Aucun dépôt à afficher" />
            ) : (
              repos.map((repo, i) => <DiagramRepoRow key={`${repo.repo}-${i}`} repo={repo} index={i} />)
            )}
          </InfraStack>

          <InfraStack
            tone="emerald"
            icon={Database}
            title="Bases de données"
            count={databasesTotal}
            summary={databaseSummary || (matchedRole ? `Rôle : ${matchedRole}` : undefined)}
            footer={[
              { icon: Server, label: 'Bases', value: databasesTotal },
              { icon: ShieldCheck, label: 'Owner', value: ownerCount },
              { icon: Activity, label: 'Connect', value: connectCount },
            ]}
          >
            {databases.length === 0 ? (
              <EmptyInfra message="Aucune base à afficher" />
            ) : (
              databases.map((db, i) => <DiagramDbRow key={`${db.database}-${i}`} db={db} index={i} />)
            )}
          </InfraStack>
        </section>
      </div>
    </div>
  );
}

function DiagramSectionTitle({ children, accent }: { children: ReactNode; accent: 'violet' | 'indigo' | 'emerald' }) {
  const color = accent === 'violet' ? 'text-violet-300' : accent === 'emerald' ? 'text-emerald-300' : 'text-indigo-300';
  return (
    <p className={`mb-4 text-center text-xs font-bold uppercase tracking-[0.28em] ${color}`}>{children}</p>
  );
}

function DiagramWires({ userCount }: { userCount: number }) {
  const spread = Math.max(userCount, 1);
  const step = 100 / (spread + 1);
  return (
    <svg
      className="pointer-events-none absolute left-0 right-0 top-[7.5rem] z-0 hidden h-[calc(100%-8rem)] w-full md:block"
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="wire-violet" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#6366f1" stopOpacity="0.4" />
        </linearGradient>
        <linearGradient id="wire-hub" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#818cf8" />
          <stop offset="100%" stopColor="#34d399" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      {Array.from({ length: spread }).map((_, i) => {
        const x = step * (i + 1);
        return (
          <path
            key={i}
            d={`M ${x} 0 L ${x} 18 Q ${x} 28 50 38 L 50 55`}
            fill="none"
            stroke="url(#wire-violet)"
            strokeWidth="1.5"
            className="diagram-flow-line"
            style={{ animationDelay: `${i * 0.2}s` }}
          />
        );
      })}
      <path d="M 50 55 L 50 72" fill="none" stroke="url(#wire-hub)" strokeWidth="2" className="diagram-flow-line" />
      <path d="M 22 72 L 22 88 Q 22 95 50 100 L 78 95 Q 78 88 78 72" fill="none" stroke="url(#wire-hub)" strokeWidth="1.5" className="diagram-flow-line" />
      <line x1="25%" y1="72%" x2="25%" y2="95%" stroke="url(#wire-hub)" strokeWidth="1.5" className="diagram-flow-line" />
      <line x1="75%" y1="72%" x2="75%" y2="95%" stroke="url(#wire-hub)" strokeWidth="1.5" className="diagram-flow-line" />
    </svg>
  );
}

function FlowConnector() {
  return (
    <div className="relative z-10 flex justify-center py-1">
      <div className="flex flex-col items-center">
        <div className="h-10 w-px bg-gradient-to-b from-violet-400/80 via-indigo-400/50 to-transparent" />
        <motion.div animate={{ y: [0, 5, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown className="h-5 w-5 text-violet-400 drop-shadow-[0_0_8px_rgba(167,139,250,0.8)]" />
        </motion.div>
      </div>
    </div>
  );
}

function DiagramUserCard({ user, botName, index }: { user: BotAccessUser; botName: string; index: number }) {
  const avatarSrc = getBzUserAvatar({ name: user.name, email: user.email });
  const initials = getBzUserInitials(user.name);

  return (
    <motion.div
      initial={{ y: -20, opacity: 0, scale: 0.92 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      transition={{ delay: 0.06 * index, type: 'spring', stiffness: 280, damping: 20 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="diagram-glow-card w-[200px] rounded-2xl p-[1px] shadow-lg shadow-violet-950/50"
    >
      <div className="rounded-2xl bg-slate-950/95 px-4 py-4 text-center backdrop-blur-sm">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-violet-400/60 shadow-[0_0_24px_rgba(139,92,246,0.45)]">
          {avatarSrc ? (
            <img src={avatarSrc} alt={user.name} className="h-full w-full object-cover" />
          ) : (
            <span className="text-lg font-black text-violet-200">{initials}</span>
          )}
        </div>
        <p className="text-sm font-black text-white">{user.name}</p>
        {user.relationType || user.isPrimary ? (
          <span className="mt-1.5 inline-block rounded-full bg-violet-500/25 px-2 py-0.5 text-[9px] font-bold uppercase text-violet-200">
            {user.isPrimary ? 'principal' : user.relationType?.replace(/_/g, ' ')}
          </span>
        ) : null}
        <p className="mt-2 text-[10px] font-semibold text-indigo-300/90">→ {botName}</p>
      </div>
    </motion.div>
  );
}

function DiagramHub({ botName, botAvatar }: { botName: string; botAvatar?: string }) {
  return (
    <motion.div
      className="relative"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 200, delay: 0.15 }}
    >
      <motion.div
        className="absolute -inset-10 rounded-full bg-indigo-500/30 blur-3xl"
        animate={{ opacity: [0.4, 0.85, 0.4], scale: [1, 1.08, 1] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute -inset-4 rounded-full border border-violet-400/30"
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
      />
      <div
        className="relative flex h-48 w-52 flex-col items-center justify-center bg-gradient-to-br from-indigo-600/50 via-slate-900/95 to-violet-900/50 p-4 text-center shadow-[0_0_60px_rgba(99,102,241,0.35)] ring-2 ring-indigo-400/50"
        style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}
      >
        {botAvatar ? (
          <img src={botAvatar} alt={botName} className="mb-2 h-14 w-14 rounded-full border-2 border-indigo-300/50 object-cover" />
        ) : null}
        <h2 className="text-3xl font-black uppercase tracking-wide text-white drop-shadow-lg">{botName}</h2>
        <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.25em] text-indigo-300">Hub opérationnel</p>
      </div>
    </motion.div>
  );
}

function InfraStack({
  tone,
  icon: Icon,
  title,
  count,
  summary,
  footer,
  children,
}: {
  tone: 'indigo' | 'emerald';
  icon: typeof GitBranch;
  title: string;
  count: number;
  summary?: string;
  footer: { icon: typeof GitBranch; label: string; value: number }[];
  children: ReactNode;
}) {
  const border = tone === 'indigo' ? 'border-indigo-500/30' : 'border-emerald-500/30';
  const glow = tone === 'indigo' ? 'from-indigo-600/20' : 'from-emerald-600/20';
  const text = tone === 'indigo' ? 'text-indigo-300' : 'text-emerald-300';
  const chip = tone === 'indigo' ? 'bg-indigo-500/15 text-indigo-200' : 'bg-emerald-500/15 text-emerald-200';

  return (
    <motion.div
      initial={{ y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: tone === 'indigo' ? 0.2 : 0.3 }}
      className={`diagram-glow-card overflow-hidden rounded-2xl ${border}`}
    >
      <div className={`relative bg-gradient-to-br ${glow} to-slate-950/95`}>
        <div className={`pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full blur-3xl ${tone === 'indigo' ? 'bg-indigo-500/25' : 'bg-emerald-500/25'}`} />
        <div className="relative border-b border-white/5 p-4">
          <div className="flex items-center gap-3">
            <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${chip}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-black text-white">{title}</h3>
              <p className={`text-sm font-bold ${text}`}>
                {count} entrée{count > 1 ? 's' : ''} — liste complète
              </p>
            </div>
          </div>
          {summary ? <p className="mt-2 text-xs leading-relaxed text-slate-400">{summary}</p> : null}
        </div>

        <div className="max-h-[min(52vh,560px)] space-y-2 overflow-y-auto p-3">{children}</div>

        <div className="grid grid-cols-3 gap-2 border-t border-white/5 bg-black/30 p-3">
          {footer.map((stat) => {
            const SIcon = stat.icon;
            return (
              <div key={stat.label} className="text-center">
                <SIcon className={`mx-auto h-4 w-4 ${text}`} />
                <p className="mt-1 text-lg font-black text-white">{stat.value}</p>
                <p className="text-[9px] uppercase tracking-wide text-slate-500">{stat.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

function EmptyInfra({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-dashed border-white/10 px-4 py-12 text-center text-sm text-slate-500">{message}</div>
  );
}

function DiagramRepoRow({ repo, index }: { repo: BotAccessGitHubRepo; index: number }) {
  const title = repo.repo || repo.repo_path || repo.url || 'Dépôt';
  const sub =
    repo.repo_path && repo.repo_path !== title
      ? repo.repo_path
      : repo.repo_path?.includes('/')
        ? repo.repo_path.split('/').slice(0, -1).join('/')
        : undefined;
  const badge = repo.access_level || (repo.verified ? 'vérifié' : repo.private ? 'privé' : null);

  return (
    <motion.div
      initial={{ x: -8, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: Math.min(index * 0.02, 0.8) }}
      className="group rounded-xl border border-indigo-500/15 bg-slate-950/70 px-3 py-2.5 transition hover:border-indigo-400/50 hover:bg-indigo-950/40 hover:shadow-[0_0_20px_rgba(99,102,241,0.15)]"
    >
      <div className="flex items-start gap-2">
        <GitBranch className="mt-0.5 h-4 w-4 shrink-0 text-indigo-400" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-white" title={title}>
            {title}
          </p>
          {sub ? <p className="truncate text-[11px] text-slate-500">{sub}</p> : null}
          {repo.url ? (
            <p className="mt-1 truncate font-mono text-[10px] text-slate-600" title={repo.url}>
              {repo.url}
            </p>
          ) : null}
        </div>
        {badge ? (
          <span className="shrink-0 rounded-md bg-indigo-500/25 px-2 py-0.5 text-[9px] font-bold uppercase text-indigo-200">
            {badge}
          </span>
        ) : null}
      </div>
    </motion.div>
  );
}

function DiagramDbRow({ db, index }: { db: BotAccessDatabase; index: number }) {
  const badge = db.is_owner ? 'owner' : db.can_connect ? 'connect' : null;

  return (
    <motion.div
      initial={{ x: 8, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: Math.min(index * 0.02, 0.8) }}
      className="group rounded-xl border border-emerald-500/15 bg-slate-950/70 px-3 py-2.5 transition hover:border-emerald-400/50 hover:bg-emerald-950/40 hover:shadow-[0_0_20px_rgba(52,211,153,0.12)]"
    >
      <div className="flex items-center gap-2">
        <Database className="h-4 w-4 shrink-0 text-emerald-400" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-white">{db.database || 'Base'}</p>
          {db.owner ? <p className="truncate text-[11px] text-slate-500">Utilisateur : {db.owner}</p> : null}
        </div>
        {badge ? (
          <span className="shrink-0 rounded-md bg-emerald-500/25 px-2 py-0.5 text-[9px] font-bold uppercase text-emerald-200">
            {badge}
          </span>
        ) : null}
      </div>
    </motion.div>
  );
}
